import {
  EXCHANGE_PROVIDER_ACTION,
  STAKING_ACTION
} from '../../../constants/actions'
import { API_RESPONSE } from '../../../constants/api'
import { getMinus180DaysTimestamp } from '../../../util/date.helpers'
import { formatAmount } from '../../../util/number.helpers'
import { mergeToNewObject } from '../../../util/object.helpers'
import { buildTransactionErrorMessage } from '../../../util/rewardBox.helpers'

export const stakingProfile = (state = INITIAL_STATE, { type, payload }) => {
  switch (type) {
    case STAKING_ACTION.CHANGE_STATE:
      return mergeToNewObject(
        state,
        { [payload.property]: payload.value },
        handleChangeState(payload)
      )

    case STAKING_ACTION.RESET_STATE:
      // return INITIAL_STATE
      return mergeToNewObject(state, {
        stakeAmount: '',
        rewardStakingPayload: null,
        loading: false,
        stakingInfoProcessing: false,
        unStakeProcessing: false,
        stakeProcessing: false,
        rewardClaimProcessing: false,
        symbolRewardClaimed: null,
        symbolRewardClaimProcessing: false,
        sdkProcessing: false,
        error: {}
      })

    case STAKING_ACTION.CHANGE_STAKE_INFO:
      const { stakedQBX, endTime } = payload
      return mergeToNewObject(state, {
        stakedQBX,
        endTime,
        startTime: getMinus180DaysTimestamp(endTime),
        stakingInfoProcessing: false
      })

    case STAKING_ACTION.GET_QBX_PRICE_SUCCESS:
      return mergeToNewObject(state, {
        priceOfQBX: parseFloat(payload.last || 0)
      })

    case STAKING_ACTION.GET_STAKING_REWARD:
      return mergeToNewObject(state, {
        loading: true
      })

    case STAKING_ACTION.GET_STAKING_REWARD_SUCCESS:
      const { releaseable } = payload
      return mergeToNewObject(state, {
        loading: false,
        stakingReward: formatAmount(releaseable)
      })

    case STAKING_ACTION.GET_STAKING_REWARD_FAILED:
      return mergeToNewObject(state, {
        loading: false,
        stakingReward: 0
      })

    case STAKING_ACTION.CLAIM_STAKING_REWARD:
      return mergeToNewObject(state, {
        rewardClaimProcessing: true,
        error: {}
      })

    case STAKING_ACTION.CLAIM_STAKING_REWARD_SUCCESS:
      return mergeToNewObject(
        state,
        {
          rewardStakingPayload: payload,
          rewardClaimProcessing: false,
          error: {}
        },
        payload
      )

    case STAKING_ACTION.CLAIM_STAKING_REWARD_FAILED:
      return mergeToNewObject(state, {
        rewardClaimProcessing: false,
        error: {
          rewardClaim: buildTransactionErrorMessage()
        }
      })

    case STAKING_ACTION.CLAIM_SYMBOL_REWARD:
      return mergeToNewObject(state, {
        symbolRewardClaimProcessing: true,
        symbolRewardClaimed: null,
        error: {}
      })

    case STAKING_ACTION.CLAIM_SYMBOL_REWARD_SUCCESS:
      const { releasedVir } = payload
      return mergeToNewObject(state, {
        symbolRewardClaimProcessing: false,
        symbolRewardClaimed: releasedVir,
        stakingReward: 0,
        claimSymbolInfo: {
          ...state.claimSymbolInfo,
          pendingTransactions: 1
        },
        error: {}
      })

    case STAKING_ACTION.CLAIM_SYMBOL_REWARD_FAILED:
      return mergeToNewObject(
        state,
        {
          symbolRewardClaimProcessing: false,
          symbolRewardClaimed: null
        },
        payload.status === API_RESPONSE.BAD_REQUEST
          ? {
              claimSymbolInfo: {
                ...state.claimSymbolInfo,
                pendingTransactions: 1
              },
              error: {
                symbolRewardClaim:
                  'brand:staking.you-have-already-claimed-staking-reward'
              }
            }
          : {
              error: {
                symbolRewardClaim: buildTransactionErrorMessage()
              }
            }
      )

    case STAKING_ACTION.GET_SYMBOL_REWARD_SUCCESS:
      return mergeToNewObject(state, {
        claimSymbolInfo: payload
      })

    case EXCHANGE_PROVIDER_ACTION.ON_CONNECT_EXCHANGE_PROVIDER_SUCCESS:
    case EXCHANGE_PROVIDER_ACTION.ON_UPDATE_EXCHANGE_PROVIDER_SUCCESS:
    case EXCHANGE_PROVIDER_ACTION.ON_CONNECT_EXCHANGE_PROVIDER_FAILED:
      return mergeToNewObject(state, {
        sdkProcessing: false
      })

    case STAKING_ACTION.GET_USER_TIER:
      return mergeToNewObject(state, { userTierLoading: true })

    case STAKING_ACTION.GET_USER_TIER_SUCCESS:
      return mergeToNewObject(state, { userTierLoading: false })

    case STAKING_ACTION.GET_USER_TIER_FAILED:
      return mergeToNewObject(state, { userTierLoading: false })

    default:
      return state
  }
}

const INITIAL_STATE = {
  availableQBX: null,
  stakedQBX: 0,
  endTime: 0,
  startTime: 0,
  priceOfQBX: 0,
  stakingReward: 0,
  stakeAmount: '',
  rewardStakingPayload: null,
  loading: false,
  stakingInfoProcessing: false,
  unStakeProcessing: false,
  stakeProcessing: false,
  rewardClaimProcessing: false,
  symbolRewardClaimProcessing: false,
  symbolRewardClaimed: null,
  claimSymbolInfo: {
    releaseable: '',
    stakingRewardWei: '',
    released: '',
    pendingTransactions: 0
  },
  sdkProcessing: false,
  userTierLoading: false,
  error: {}
}

export const STAKING_PROFILE_DISPATCH = {
  LOADING: 'loading',
  AVAILABLE_QBX: 'availableQBX',
  STAKED_QBX: 'stakedQBX',
  END_TIME: 'endTime',
  STAKING_INFO_PROCESSING: 'stakingInfoProcessing',
  UN_STAKE_PROCESSING: 'unStakeProcessing',
  STAKE_PROCESSING: 'stakeProcessing',
  REWARD_CLAIM_PROCESSING: 'rewardClaimProcessing',
  SYMBOL_REWARD_CLAIM_PROCESSING: 'symbolRewardClaimProcessing',
  STAKING_REWARD: 'stakingReward',
  SDK_PROCESSING: 'sdkProcessing',
  ERROR: 'error',
  STAKE_AMOUNT: 'stakeAmount'
}

const handleChangeState = ({ property }) => {
  if (property === STAKING_PROFILE_DISPATCH.ERROR) {
    return {
      unStakeProcessing: false,
      stakeProcessing: false,
      rewardClaimProcessing: false,
      symbolRewardClaimProcessing: false,
      sdkProcessing: false
    }
  } else if (
    [
      STAKING_PROFILE_DISPATCH.UN_STAKE_PROCESSING,
      STAKING_PROFILE_DISPATCH.STAKE_PROCESSING,
      STAKING_PROFILE_DISPATCH.REWARD_CLAIM_PROCESSING,
      STAKING_PROFILE_DISPATCH.SYMBOL_REWARD_CLAIM_PROCESSING,
      STAKING_PROFILE_DISPATCH.SDK_PROCESSING
    ].includes(property)
  ) {
    return {
      error: {}
    }
  }
  return {}
}
